// CYBORG-1416: the agent pane header's live facts: the active goal, the background shells still
// running and a provider retry in flight. The daemon builds this (server cyborg/agent-activity.ts)
// and pushes it as cyborg:agent_activity; fetch_agent_state returns it on a reload.
import { relativeTime } from "$lib/utils/datetime.js";
import { formatSubagentElapsed } from "./provider-subagents.js";

export interface AgentGoal {
  condition: string;
  status: "active" | "paused";
  setAt: number;
  // The CLI reports it when it knows; null reads as "not known", never as zero.
  turns: number | null;
}

export interface BackgroundTask {
  id: string;
  command: string;
  startedAt: number;
}

// The provider's subscription limit in force (server cyborg/agent-activity.ts). utilization is a
// percent 0-100, resetsAt epoch ms; either is null when the provider does not say.
export interface AgentRateLimit {
  limit: string;
  status: "ok" | "warning" | "rejected";
  utilization: number | null;
  resetsAt: number | null;
}

export type McpServerStatus =
  | "connected"
  | "pending"
  | "failed"
  | "needs_auth"
  | "disabled"
  | "cancelled";

export interface McpServerState {
  name: string;
  status: McpServerStatus;
  // Already redacted by the daemon.
  error?: string;
}

// One background process of the session, finished ones included (the daemon keeps them 30 min).
// Sent by daemons that capture process output (#4861); an older daemon or Codex sends only `tasks`.
export interface AgentProcess {
  processId: string;
  command: string;
  startedAt: number;
  status: "running" | "completed" | "failed" | "stopped";
  exitCode: number | null;
  endedAt: number | null;
  // The daemon is capturing this process's output (tail + live frames answer for it).
  hasOutput: boolean;
  // The timeline tool call that started it (Codex). Absent from an older daemon.
  callId?: string;
}

export interface AgentActivity {
  goal: AgentGoal | null;
  tasks: BackgroundTask[];
  processes?: AgentProcess[];
  // max null: the provider retries without a bound, or does not say how many.
  retry: { attempt: number; max: number | null } | null;
  rateLimit?: AgentRateLimit | null;
  mcp?: McpServerState[];
}

export const EMPTY_ACTIVITY: AgentActivity = { goal: null, tasks: [], retry: null };

// The pill is for a goal that is working. A paused one has nothing to clear from the header.
export function activeGoal(activity: AgentActivity): AgentGoal | null {
  return activity.goal?.status === "active" ? activity.goal : null; // null: no active goal
}

/** "set 6m ago · 3 turns" */
export function goalAgeLabel(goal: AgentGoal, now: number = Date.now()): string {
  const parts = [`set ${relativeTime(goal.setAt, now)}`];
  if (goal.turns !== null) parts.push(`${goal.turns} ${goal.turns === 1 ? "turn" : "turns"}`);
  return parts.join(" · ");
}

/** The header's process list: the daemon's `processes` when it sends them, else the running
 *  `tasks` (Codex, an older daemon: no output captured). Chosen by the data, never a flag. */
export function activityProcesses(activity: AgentActivity): AgentProcess[] {
  if (activity.processes?.length) return activity.processes;
  return activity.tasks.map((t) => ({
    processId: t.id,
    command: t.command,
    startedAt: t.startedAt,
    status: "running",
    exitCode: null,
    endedAt: null,
    hasOutput: false,
  }));
}

/** The tool calls whose process still runs: their card says running even after the turn
 *  ended (Codex cancels the call while the background command goes on). */
export function runningCallIds(activity: AgentActivity): Set<string> {
  const ids = new Set<string>();
  for (const p of activity.processes ?? [])
    if (p.callId && p.status === "running") ids.add(p.callId);
  return ids;
}

/** "2 background processes" */
export function processesLabel(count: number): string {
  return `${count} background ${count === 1 ? "process" : "processes"}`;
}

/** "retrying 2/10", or "retrying 2" when the provider gives no bound */
export function retryLabel(retry: { attempt: number; max: number | null }): string {
  return retry.max === null
    ? `retrying ${retry.attempt}`
    : `retrying ${retry.attempt}/${retry.max}`;
}

/** The typing line's usage-limit suffix, "usage limit reached, resumes at 14:05". Only a rejected
 *  limit with a known reset says it; a warning is not a stop. null: nothing to say. */
export function usageLimitLabel(limit: AgentRateLimit | null | undefined): string | null {
  if (!limit || limit.status !== "rejected" || limit.resetsAt === null) return null; // null: no stop to explain
  const at = new Date(limit.resetsAt);
  const hh = String(at.getHours()).padStart(2, "0");
  const mm = String(at.getMinutes()).padStart(2, "0");
  return `usage limit reached, resumes at ${hh}:${mm}`;
}

/** "4m", "12s · exit 1": a process row's clock (the subagent rows' own elapsed format). */
export function processMeta(p: AgentProcess, now: number = Date.now()): string {
  const ms = (p.endedAt ?? now) - p.startedAt;
  const clock = ms >= 0 ? formatSubagentElapsed(ms) : "";
  if (p.status === "running") return clock;
  const end = p.exitCode !== null ? `exit ${p.exitCode}` : p.status;
  return clock ? `${clock} · ${end}` : end;
}
